/**
 * Buddy's proposal as the trainer previews it (ai-plan-builder R15 to R19; boards S, S2, S3): one
 * component for the builder's assistant and the Buddy page.
 *
 * The changes stand grouped by day and under „Struktura planu", each with its kind, title, before
 * and after, its reason, a soft warning when it has one (R22), and accept and reject toggles. A new
 * day and the exercises on it are one pair, decided together. A change the trainer's own edits have
 * overtaken is outdated, computed live from the draft, and has no toggles (R18). Nothing enters the
 * draft but through „Zastosuj" (R17), and everything applied goes back with one undo (R19).
 *
 * ── Where the draft is ───────────────────────────────────────────────────────────────────────
 * The changes are applied through the plan document store (`applyProposal`). In the builder the
 * store holds the plan; on the Buddy page it holds the Buddy draft only while one is hosted. So the
 * page says which tree the proposal stands against (`ProposalHost.draftOf`) and makes the store hold
 * it before an apply (`hold`); the card does the rest the same way in both places.
 *
 * ── A change to an exercise is the builder's card (board U) ──────────────────────────────────
 * An alter, a replacement and an addition are drawn as the exercise card the builder draws, read-only,
 * with what moved marked in place (`model/exerciseDrawing.ts` decides which). The card belongs to
 * `features/plan-day-programming` and its progression row to `features/plan-progression`, which this
 * feature may not import, so the host draws it (`ProposalHost.renderExerciseChange`).
 *
 * ── Decisions are recorded, never acted on by the server ─────────────────────────────────────
 * Every toggle, the two „wszystkie" links, the apply and the undo say what they did (R53); the
 * record is statistics, and an apply recorded is what ends the server's wait on the proposal.
 */

import clsx from 'clsx';
import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { AiChangeDecision, ProposalStanding } from '~/entities/ai-conversation';
import {
  appliedChanges,
  applyProposal,
  outdatedChanges,
  presentChanges,
  proposalFacts,
  readPlanDraftRevision,
  undoPlanDraftEdit,
  useAiApplyStanding,
  usePlanDraftRevision,
  usePlanDraftTree,
  type AiProposal,
  type ExerciseDraft,
  type PlanDraft,
} from '~/entities/plan-draft';
import { CheckGlyph, CloseGlyph } from '~/shared/ui/FormGlyphs';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import { WarningGlyph } from '~/shared/ui/WarningGlyph';

import { changeWords, exerciseCardWords, headingWords } from '../lib/proposalWording';
import type { ExerciseBaseline } from '../model/exerciseDrawing';
import { choose, chooseAll, chooseDay, initialChoices, proposalCardView, type ChangeChoices, type ProposalRow } from '../model/proposalCard';

import { LinkGlyph } from './ChatGlyphs';
import { ModelText } from './Messages';
import surfaces from './surfaces.module.css';
import styles from './ProposalCard.module.css';

/** Where proposals are applied: the builder's plan, or a Buddy draft the page hosts. */
export interface ProposalHost {
  /** Which surface: the builder words the applied card for the week beside it, the page for the draft. */
  readonly where: 'builder' | 'page';
  /**
   * The draft a proposal made on Buddy draft `draftTurnId` (`null`: the builder's own plan) stands
   * against now, given the document store's tree; `null` when there is none to read.
   */
  readonly draftOf: (draftTurnId: number | null, storeTree: PlanDraft | null) => PlanDraft | null;
  /** Make the document store hold that draft, so an apply writes into it; answers whether it does. */
  readonly hold: (draftTurnId: number | null) => boolean;
  /**
   * A change to one exercise, drawn as the builder's exercise card with the change marked (board U):
   * the day programming card over the progression statement, two features this one may not import.
   */
  readonly renderExerciseChange: (change: ExerciseChangeSlot) => ReactNode;
}

/** What the host draws a change to one exercise from. */
export interface ExerciseChangeSlot {
  /** The exercise as the change writes it. */
  readonly exercise: ExerciseDraft;
  /** What it is compared with: the exercise it alters, nothing (every set new), or nothing to mark. */
  readonly baseline: ExerciseBaseline;
  /** The builder's marker for its place in the day (`2`, `2b`); `null` for a warm-up or when unknown. */
  readonly ordinal: string | null;
  /** The day's own rest, in seconds; `null` for the plan's default. */
  readonly dayRestSeconds: number | null;
  /** The card head's right end: „4 → 5 serii", „Nowe w miejscu 2", „Nowe, na końcu dnia". */
  readonly tag: string | null;
  /** The increment each rule states, for the progression row („+2,5 kg / cykl"). */
  readonly increments: { readonly before: string | null; readonly after: string | null };
}

export interface ProposalCardProps {
  readonly proposal: AiProposal;
  readonly standing: ProposalStanding;
  readonly recorded: ReadonlyMap<string, AiChangeDecision>;
  readonly draftTurnId: number | null;
  readonly host: ProposalHost;
  /** Nothing is running: an apply or a reject with a reason may start. */
  readonly ready: boolean;
  /** Record decisions on changes (R53). */
  readonly onRecord: (decisions: ReadonlyMap<string, AiChangeDecision>) => void;
  /** „Odrzuć z uwagą": the trainer says in the composer what to change about this change (R16). */
  readonly onRejectWithReason: (changeId: string, title: string) => void;
  /** „Inna wersja": Buddy asked for another proposal on the same draft (R11); `null` where not offered. */
  readonly onAnotherVersion: (() => void) | null;
}

export function ProposalCard(props: ProposalCardProps) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);
  if (props.standing !== 'replaced') return <ProposalBody {...props} />;
  // A proposal a later message replaced (R16): one line, and the card read-only on request.
  return (
    <>
      <p className={styles.replaced}>
        <span>
          <b>{t('web:aiPlan.proposal.replacedTitle')}</b>
          {` · ${t('web:aiPlan.proposal.replacedNote')}`}
        </span>
        <button
          type="button"
          className={styles.link}
          aria-expanded={shown}
          onClick={() => {
            setShown((current) => !current);
          }}
        >
          {shown ? t('web:aiPlan.proposal.hide') : t('web:aiPlan.proposal.show')}
        </button>
      </p>
      {shown && <ProposalBody {...props} />}
    </>
  );
}

function ProposalBody({ proposal, standing, recorded, draftTurnId, host, ready, onRecord, onRejectWithReason, onAnotherVersion }: ProposalCardProps) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const storeTree = usePlanDraftTree();
  const revision = usePlanDraftRevision();
  const inSession = useAiApplyStanding(proposal);
  const draft = host.draftOf(draftTurnId, storeTree);
  const [choices, setChoices] = useState<ChangeChoices>(() => initialChoices(proposal, recorded));
  // The apply made here, while it is still the last thing done to the tree: what „Cofnij" takes back.
  const [applied, setApplied] = useState<{ readonly ids: readonly string[]; readonly revision: number } | null>(null);
  // Taken back here: under review again, also in the moment before the server says it waits again.
  const [undone, setUndone] = useState(false);
  // What the last apply or undo did, said in a region that stands before it speaks.
  const [announcement, setAnnouncement] = useState('');
  // The control owed focus once the card has redrawn: the one that took the place of the one pressed.
  const pendingFocus = useRef<'undo' | 'apply' | null>(null);
  const undoButton = useRef<HTMLButtonElement>(null);

  const applyButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const target = pendingFocus.current;
    if (target === null) return;
    // Held until the control is drawn: the card may redraw on the tree before it redraws on its state.
    const control = (target === 'undo' ? undoButton : applyButton).current;
    if (control === null) return;
    pendingFocus.current = null;
    control.focus();
  });

  if (draft === null) return null;
  const outdated = outdatedChanges(proposal, draft);
  const present = presentChanges(proposal, draft);
  // What no toggle decides on: an outdated change, and one whose result already stands.
  const settled = new Set([...outdated, ...present]);
  const facts = proposalFacts(proposal, draft);
  const dayOrder = (slotToken: string) => draft.phases.flatMap((phase) => phase.days).findIndex((day) => day.slotToken === slotToken);
  const view = proposalCardView(facts, outdated, present, choices, dayOrder);

  // Applied is what an apply made and an undo (the card's or the builder's) has not taken back; what
  // the server has on record answers only where this session has made no apply (`appliedChanges`).
  const recordedApplied = [...recorded].filter(([, decision]) => decision === 'applied').map(([id]) => id);
  const stillApplied = appliedChanges(proposal, draft, inSession, recordedApplied);
  const justApplied = applied !== null && revision === applied.revision;
  const isApplied = stillApplied.size > 0;
  const reviewing = !isApplied && (standing === 'review' || standing === 'applied' || (undone && standing === 'closed'));
  const undoable = justApplied;
  const appliedCount = stillApplied.size;

  const decide = (next: { readonly choices: ChangeChoices; readonly decided: ReadonlyMap<string, AiChangeDecision> }) => {
    setChoices(next.choices);
    if (next.decided.size > 0) onRecord(next.decided);
  };

  const apply = () => {
    if (!ready || view.accepted.length === 0 || !host.hold(draftTurnId)) return;
    const made = applyProposal(proposal, view.accepted);
    if (made.size === 0) return;
    setApplied({ ids: [...made], revision: readPlanDraftRevision() });
    setAnnouncement(t('web:aiPlan.proposal.applied', { count: made.size }));
    setUndone(false);
    pendingFocus.current = 'undo';
    onRecord(new Map([...made].map((id): [string, AiChangeDecision] => [id, 'applied'])));
  };

  const undo = () => {
    if (applied === null || !undoable) return;
    undoPlanDraftEdit();
    // Accepted, no longer applied: the server waits on the proposal again.
    onRecord(new Map(applied.ids.map((id): [string, AiChangeDecision] => [id, 'accepted'])));
    setApplied(null);
    setAnnouncement(t('web:aiPlan.proposal.undone'));
    setUndone(true);
    pendingFocus.current = 'apply';
  };

  const status = (
    <p className={hidden.srOnly} role="status">
      {announcement}
    </p>
  );

  if (isApplied) {
    return (
      <>
        {status}
        <section className={clsx(surfaces.card, styles.card, surfaces.riseCard)} aria-labelledby={titleId}>
          <header className={styles.head}>
            <h3 id={titleId} className={styles.title}>
              {t('web:aiPlan.proposal.appliedTitle', { count: appliedCount })}
            </h3>
          </header>
          <div className={clsx(styles.foot, styles.footApplied)}>
            <p className={styles.done}>
              <CheckGlyph className={styles.doneGlyph} />
              {t('web:aiPlan.proposal.applied', { count: appliedCount })}
            </p>
            {undoable && (
              <button ref={undoButton} type="button" className={styles.link} onClick={undo}>
                {t('web:aiPlan.proposal.undo')}
              </button>
            )}
          </div>
        </section>
        {host.where === 'builder' && <p className={styles.aside}>{t('web:aiPlan.proposal.markedNote')}</p>}
      </>
    );
  }

  const disabledId = `${titleId}-apply`;
  const row = (each: ProposalRow, paired: boolean) => {
    const words = changeWords(t, i18n.language, each.facts);
    const id = each.facts.change.id;
    const rejected = !each.outdated && !each.inPlan && each.choice === 'rejected';
    const { drawing } = each;
    const cardWords = drawing === null ? null : exerciseCardWords(t, i18n.language, drawing);
    return (
      <Fragment key={id}>
      <div className={clsx(styles.change, rejected && styles.rejected, each.outdated && styles.outdated, paired && styles.paired)}>
        <span className={styles.kind}>
          {each.outdated ? t('web:aiPlan.proposal.outdated') : each.inPlan ? t('web:aiPlan.proposal.inPlan') : words.kind}
        </span>
        <span className={styles.changeTitle}>{words.title}</span>
        {drawing === null && (words.before !== null || words.after !== null) && (
          <span className={styles.detail}>
            {words.before !== null && <s className={styles.before}>{words.before}</s>}
            {words.before !== null && words.after !== null && ' → '}
            {words.after}
          </span>
        )}
        {each.outdated ? (
          <span className={styles.reason}>{words.outdated}</span>
        ) : (
          <ModelText text={each.facts.change.reason} className={styles.reason} />
        )}
        {!each.outdated &&
          each.facts.change.warnings.map((warning, index) => (
            <span key={index} className={styles.warning}>
              <WarningGlyph className={styles.warningGlyph} />
              {warning.message}
            </span>
          ))}
        {drawing !== null && cardWords !== null && (
          <div className={styles.exercise}>
            {cardWords.replaced !== null && (
              <p className={styles.gone}>
                <span className={styles.goneLabel}>{t('web:aiPlan.proposal.card.was')}</span>
                <s>{cardWords.replaced}</s>
              </p>
            )}
            {host.renderExerciseChange({
              exercise: drawing.exercise,
              baseline: drawing.baseline,
              ordinal: drawing.ordinal,
              dayRestSeconds: drawing.dayRestSeconds,
              tag: cardWords.tag,
              increments: cardWords.increments,
            })}
          </div>
        )}
        {reviewing && !each.outdated && !each.inPlan && (
          <div className={styles.toggles} role="group" aria-label={words.title}>
            <button
              type="button"
              className={clsx(styles.toggle, each.choice === 'accepted' && styles.toggleOn)}
              aria-pressed={each.choice === 'accepted'}
              aria-label={t('web:aiPlan.proposal.accept', { title: words.title })}
              onClick={() => {
                decide(choose(proposal, choices, id, 'accepted', settled));
              }}
            >
              <CheckGlyph className={styles.toggleGlyph} />
            </button>
            <button
              type="button"
              className={clsx(styles.toggle, each.choice === 'rejected' && styles.toggleOn)}
              aria-pressed={each.choice === 'rejected'}
              aria-label={t('web:aiPlan.proposal.reject', { title: words.title })}
              onClick={() => {
                decide(choose(proposal, choices, id, 'rejected', settled));
              }}
            >
              <CloseGlyph className={styles.toggleGlyph} />
            </button>
          </div>
        )}
      </div>
      {reviewing && rejected && (
        <div className={styles.rejectRow}>
          <button
            type="button"
            className={clsx(surfaces.button, surfaces.ghost, surfaces.small)}
            aria-disabled={ready ? undefined : 'true'}
            onClick={() => {
              if (ready) onRejectWithReason(id, words.title);
            }}
          >
            {t('web:aiPlan.proposal.rejectWithReason')}
          </button>
        </div>
      )}
      </Fragment>
    );
  };

  return (
    <>
      {status}
      <section className={clsx(surfaces.card, styles.card, surfaces.riseCard)} aria-labelledby={titleId}>
        <header className={styles.head}>
          <h3 id={titleId} className={styles.title}>
            {t('web:aiPlan.proposal.title', { count: view.reviewable })}
          </h3>
          {reviewing && view.reviewable > 0 && (
            <button
              type="button"
              className={styles.link}
              onClick={() => {
                decide(chooseAll(proposal, choices, 'accepted', settled));
              }}
            >
              {t('web:aiPlan.proposal.acceptAll')}
            </button>
          )}
        </header>
        {view.marksSets && (
          // What the marks on the cards mean. Drawn for the eye: every mark is said in words inside
          // its own cell.
          <p className={styles.legend} aria-hidden="true">
            <span>
              <s>{t('web:aiPlan.proposal.card.legendBeforeSample')}</s> {t('web:aiPlan.proposal.card.legendBefore')}
            </span>
            <span>
              <b>{t('web:aiPlan.proposal.card.legendAddedSample')}</b> {t('web:aiPlan.proposal.card.legendAdded')}
            </span>
            <span>
              <s>{t('web:aiPlan.proposal.card.legendRemovedSample')}</s> {t('web:aiPlan.proposal.card.legendRemoved')}
            </span>
          </p>
        )}
        {view.groups.map((group) => (
          <div key={group.key} className={styles.group}>
            <div className={styles.headingRow}>
              <h4 className={styles.heading}>{headingWords(t, group.heading)}</h4>
              {reviewing && group.heading.kind === 'day' && (
                <button
                  type="button"
                  className={styles.link}
                  onClick={() => {
                    if (group.heading.kind === 'day') decide(chooseDay(proposal, choices, facts, group.heading.day.slotToken, settled));
                  }}
                >
                  {t('web:aiPlan.proposal.acceptDay')}
                  <span className={hidden.srOnly}>{` ${headingWords(t, group.heading)}`}</span>
                </button>
              )}
            </div>
            {group.items.map((item) =>
              item.kind === 'row' ? (
                row(item.row, false)
              ) : (
                <div key={item.rows.map((each) => each.facts.change.id).join(':')} className={styles.pair}>
                  <p className={styles.pairNote}>
                    <LinkGlyph className={styles.pairGlyph} />
                    {t('web:aiPlan.proposal.pairNote')}
                  </p>
                  {item.rows.map((each) => row(each, true))}
                </div>
              ),
            )}
          </div>
        ))}
        {reviewing && (
          <div className={clsx(styles.foot, styles.footReview)}>
            {/* Board U: the two lesser acts on a row, the apply across the card under them. */}
            <div className={styles.footActs}>
              {onAnotherVersion !== null && (
                <button type="button" className={styles.link} onClick={onAnotherVersion}>
                  {t('web:aiPlan.proposal.anotherVersion')}
                </button>
              )}
              <button
                type="button"
                className={clsx(surfaces.button, surfaces.ghost, styles.rejectAll)}
                onClick={() => {
                  decide(chooseAll(proposal, choices, 'rejected', settled));
                }}
              >
                {t('web:aiPlan.proposal.rejectAll')}
              </button>
            </div>
            <button
              ref={applyButton}
              type="button"
              className={clsx(surfaces.button, surfaces.primary, styles.applyWide)}
              aria-disabled={ready && view.accepted.length > 0 ? undefined : 'true'}
              aria-describedby={view.accepted.length === 0 ? disabledId : undefined}
              onClick={apply}
            >
              {t('web:aiPlan.proposal.apply', { count: view.accepted.length })}
            </button>
            {view.accepted.length === 0 && (
              <span id={disabledId} className={hidden.srOnly}>
                {t('web:aiPlan.proposal.applyNone')}
              </span>
            )}
          </div>
        )}

      </section>
      {reviewing && host.where === 'page' && <p className={styles.aside}>{t('web:aiPlan.proposal.pageNote')}</p>}
    </>
  );
}
